import { ChevronsUpDownIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrganizationListItemType } from '../../common/organization';
import { Button } from '../ui/button';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Switch } from '../ui/switch';

export type SelectOrganizationPopoverProps = {
	organizations: OrganizationListItemType[];
	value: string | null;
	onChange: (value: string) => void;
	addAllItem?: boolean;
	asFilter?: boolean;
	/** Hide deactivated organizations behind a "Show inactive" switch (off by default) */
	inactiveToggle?: boolean;
	/** Controlled state of the "Show inactive" switch, for callers that also act on it (e.g. listing inactive orgs' assets) */
	showInactive?: boolean;
	onShowInactiveChange?: (showInactive: boolean) => void;
};

export function SelectOrganizationPopover({
	organizations,
	value,
	onChange,
	addAllItem = false,
	asFilter = false,
	inactiveToggle = false,
	showInactive: controlledShowInactive,
	onShowInactiveChange,
}: SelectOrganizationPopoverProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [uncontrolledShowInactive, setUncontrolledShowInactive] = useState(false);
	const showInactive = controlledShowInactive ?? uncontrolledShowInactive;
	const setShowInactive = (checked: boolean) => {
		setUncontrolledShowInactive(checked);
		onShowInactiveChange?.(checked);
	};
	const visibleOrganizations =
		inactiveToggle && !showInactive ? organizations.filter((org) => org.enabled !== false) : organizations;
	const onChangeWrapper = (value: string) => {
		onChange(value);
		setOpen(false);
	};
	// If asFilter is true and value is 'all', we show 'All Organizations' instead of trying to find an org with id 'all'
	// With "Show inactive" on, say so on the trigger too: the switch is out of sight once the dropdown closes
	const allLabel =
		inactiveToggle && showInactive
			? t('assets.organization.allOrganizationsInclInactive')
			: t('assets.organization.allOrganizations');
	const valueLabel =
		asFilter && value === 'all'
			? allLabel
			: organizations.find((org) => org.id === value)?.name || t('assets.organization.selectOrganization');
	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					className="bg-white border text-sm rounded px-2 py-1 focus:ring-1 focus:ring-blue-600 outline-none justify-between w-full min-h-full"
					title={valueLabel}
				>
					<span className="truncate">{valueLabel}</span>
					<ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="p-0 w-(--radix-popover-trigger-width)">
				<Command>
					<CommandInput placeholder={t('assets.organization.searchPlaceholder')} />
					<CommandList>
						<CommandEmpty>{t('assets.organization.empty')}</CommandEmpty>
						{asFilter && addAllItem && (
							<CommandItem key="all" onSelect={onChangeWrapper} value="all" keywords={['all', 'any', 'none']}>
								{t('assets.organization.allOrganizations')}
							</CommandItem>
						)}
						{visibleOrganizations.map((org) => (
							<CommandItem key={org.id} onSelect={onChangeWrapper} value={org.id} keywords={[org.name || '']}>
								{org.name}
								{org.enabled === false && (
									<span className="ml-auto text-xs text-muted-foreground">{t('common.states.inactive')}</span>
								)}
							</CommandItem>
						))}
					</CommandList>
				</Command>
				{inactiveToggle && (
					<label className="flex items-center justify-between gap-2 border-t px-3 py-2 text-sm cursor-pointer">
						{t('assets.organization.showInactive')}
						{/* The default unchecked track (--input) is white in light mode, invisible on the white popover */}
						<Switch
							size="sm"
							checked={showInactive}
							onCheckedChange={setShowInactive}
							className="data-[state=unchecked]:bg-zinc-300 dark:data-[state=unchecked]:bg-input/80"
						/>
					</label>
				)}
			</PopoverContent>
		</Popover>
	);
}
